import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { LabeledValue } from "components/designV2/labeledValue/labeledValue";
import { CustomIcon } from "components/icon/customIcon.component";
import SimflexityPayLogo from "images/logo/svg/simflexityPay-logo.svg";
import { PaymentMethod } from "model/Gig";
import { CompanyWorkerSalary } from "model/Salary";
import { useTranslation } from "react-i18next";
import { Logo, LogoContainer } from "./salary.styled";
import {
  AvatarAndActions,
  RowCard,
  RowGrid,
  RowMetrics,
  RowTop,
} from "./salaryListRow.styles";

export interface SalaryListRowProps {
  salary: CompanyWorkerSalary;
  avatarUrl?: string;
  onSelectWorker: (workerId: number) => void;
}

export const SalaryListRow = ({
  salary,
  avatarUrl,
  onSelectWorker,
}: SalaryListRowProps): JSX.Element => {
  const { t } = useTranslation();

  const isTrainee = salary.employment.employmentType === "Trainee";
  const isSimflexityEmployee =
    salary.employment.employmentType === "SimflexityEmployee";
  const isSimflexityPay =
    salary.employment.paymentType === PaymentMethod.SIMFLEXITY_PAY;
  const currency = "kr";

  const title = `${salary.firstName} ${salary.lastName}${
    !salary.isActive ? ` ${t("Salary.Inactive")}` : ""
  }`;

  const salaryLabel = isTrainee
    ? t("Salary.Salary")
    : t(`Salary.${salary.employment.salaryType}`);
  const salaryValue = isTrainee
    ? "-"
    : `${salary.employment.salary} ${currency}`;

  const workPercentageValue = isTrainee
    ? "-"
    : salary.employment.salaryType === "Monthly"
      ? `${salary.employment.workPercentage * 100}%`
      : t("Salary.Hours");

  const taxValue =
    isTrainee || isSimflexityEmployee ? "-" : salary.employment.taxValue;

  const startDate = salary.employment.startDate.split("T")[0];
  const applyFromValue = salary.isActive
    ? startDate
    : `${startDate} - ${salary.employment.endDate.split("T")[0]}`;

  return (
    <RowCard
      type="button"
      $isInactive={!salary.isActive}
      onClick={() => onSelectWorker(salary.workerId)}
      aria-label={t("Staff.ProfilePanelOpenLabel", {
        worker: `${salary.firstName} ${salary.lastName}`,
      })}
    >
      <RowGrid>
        <RowTop>
          <AvatarChip
            title={title}
            subTitle={t("General.Giggers")}
            avatarUrl={avatarUrl}
            titleIcon={
              isSimflexityPay ? (
                <LogoContainer>
                  <Logo src={SimflexityPayLogo} alt="SimflexityPay logo" />
                </LogoContainer>
              ) : null
            }
          />
          <AvatarAndActions>
            <CustomIcon name="v2/right-chevron" size="18px" />
          </AvatarAndActions>
        </RowTop>

        <RowMetrics>
          <LabeledValue
            variant="small"
            value={salaryValue}
            label={salaryLabel}
          />
          <LabeledValue
            variant="small"
            value={workPercentageValue}
            label={t("Salary.WorkPercentage")}
          />
          <LabeledValue
            variant="small"
            value={taxValue.toString()}
            label={t("OrganizationalStructure.TaxTable")}
          />
          <LabeledValue
            variant="small"
            value={t(
              `OrganizationalStructure.${salary.employment.employmentType}`,
            )}
            label={t("OrganizationalStructure.EmploymentType")}
          />
          <LabeledValue
            variant="small"
            value={applyFromValue}
            label={t("Salary.ApplyFrom")}
          />
        </RowMetrics>
      </RowGrid>
    </RowCard>
  );
};
